import { inject } from '@angular/core';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { firstValueFrom } from 'rxjs';
import { CompanyInfoModel } from 'src/app/company/commpany.model';
import { CompanyService } from 'src/app/company/company.service';
import { US_ZIP_PATTERN } from 'src/app/company/companyinformation/patterns';
import { IOperatorClass } from './OperatorClass';
import { OperatorInput, OperatorOutput } from './models/models';

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const ZIP_PATTERN = new RegExp(US_ZIP_PATTERN);

export class CompanyInfoOperator implements IOperatorClass {
  private readonly companyService = inject(CompanyService);

  private getCellValue(worksheet: any, row: number): string {
    const value = worksheet.rows(row).cells(1).value;
    return value != null ? String(value).trim() : '';
  }

  private validate(info: CompanyInfoModel, groupCode: string): string | null {
    if (!info.billingno) {
      return 'ISO Billing Number is required.';
    }
    if (!info.billingno.toUpperCase().startsWith(groupCode.toUpperCase())) {
      return `ISO Billing Number must start with the group code "${groupCode}".`;
    }
    if (info.billingno.length > 6) {
      return 'ISO Billing Number must not exceed 6 characters.';
    }
    if (info.billingno.length > 4 && !/^[a-zA-Z]+$/.test(info.billingno.substring(4))) {
      return 'ISO Billing Number: characters after position 4 must be letters only.';
    }

    if (!info.compname) {
      return 'Company Name is required.';
    }

    if (!info.preparer) {
      return "Preparer's Name is required.";
    }

    if (!info.reportofficer) {
      return 'Email Address is required.';
    }
    if (!EMAIL_PATTERN.test(info.reportofficer)) {
      return 'Email Address is not valid.';
    }

    if (info.additionalemail && !EMAIL_PATTERN.test(info.additionalemail)) {
      return 'Additional Email Address is not valid.';
    }

    if (!info.title) {
      return 'Title is required.';
    }

    if (!info.address) {
      return 'Address is required.';
    }

    if (!info.city) {
      return 'City is required.';
    }

    if (!info.state) {
      return 'State is required.';
    }

    if (!info.zipcode) {
      return 'Zip Code is required.';
    }
    if (!ZIP_PATTERN.test(info.zipcode)) {
      return 'Zip Code must be a valid US zip code (e.g. 12345 or 12345-6789).';
    }

    if (!info.phone) {
      return 'Phone Number is required.';
    }
    if (info.phone.length < 7 || info.phone.length > 14) {
      return 'Phone Number must be between 7 and 14 characters.';
    }
    if (!isValidPhoneNumber(info.phone, 'US')) {
      return 'Phone Number is not a valid US phone number.';
    }
    return null;
  }

  async runOperator(operatorInput: OperatorInput): Promise<OperatorOutput> {
    const worksheet = operatorInput.workbook?.worksheets(0);
    if (!worksheet) {
      return {
        status: 'error',
        message: ['Company Information sheet not found.'],
        operatorInput,
      };
    }

    const companyInfo = new CompanyInfoModel();
    companyInfo.billingno = this.getCellValue(worksheet, 0).toUpperCase();
    companyInfo.compname = this.getCellValue(worksheet, 1);
    companyInfo.preparer = this.getCellValue(worksheet, 2);
    companyInfo.reportofficer = this.getCellValue(worksheet, 3);
    companyInfo.additionalemail = this.getCellValue(worksheet, 4);
    companyInfo.title = this.getCellValue(worksheet, 5);
    companyInfo.address = this.getCellValue(worksheet, 6);
    companyInfo.city = this.getCellValue(worksheet, 7);
    companyInfo.state = this.getCellValue(worksheet, 8);
    companyInfo.zipcode = this.getCellValue(worksheet, 9);
    companyInfo.phone = this.getCellValue(worksheet, 10);
    
    companyInfo.is_template_upload = true;

    const validationError = this.validate(companyInfo, operatorInput.prfGroup ?? '');
    if (validationError) {
      return { status: 'error', message: [validationError], operatorInput };
    }

    let resp: any;
    try {
      const save$ = this.companyService.saveCompanyInformation(
        operatorInput.prfyear,
        'Add',
        companyInfo,
        operatorInput.prfGroup,
        false,
      );
      if (!save$) {
        return {
          status: 'error',
          message: ['Failed to save company information.'],
          operatorInput,
        };
      }
      resp = await firstValueFrom(save$);
    } catch {
      return {
        status: 'error',
        message: ['Failed to save company information.'],
        operatorInput,
      };
    }

    if (resp?.message !== 'Success') {
      return {
        status: 'error',
        message: [resp?.status_msg ?? 'Failed to save company information.'],
        operatorInput,
      };
    }

    const errorStatuses = [
      'Company already exist',
      'Group Company already exist',
      'Stand-alone company already exist',
      'The company is merged',
    ];
    if (errorStatuses.includes(resp.status_msg)) {
      operatorInput.prfCreated = false;
      return { status: 'error', message: [resp.status_msg], operatorInput };
    }

    operatorInput.prfCreated = true;
    operatorInput.isGroupReporting = companyInfo.groupreport === 'on';
    return { status: 'success', operatorInput };
  }
}
